import React from "react";
import { Route, Navigate } from "react-router-dom";
import MainLayout from "@/components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";
import ErrorBoundaryWithFallback from "@/components/ErrorBoundary/ErrorBoundaryWithFallback";

// Lazy-loaded route components
const CallIntelligenceDashboard = React.lazy(
  () =>
    import(
      "@/modules/callIntelligence/features/Dashboard/components/CallIntelligenceDashboard"
    ),
);
const UnifiedCallDetail = React.lazy(
  () =>
    import(
      "@/modules/callIntelligence/features/CallReviews/components/UnifiedCallDetail"
    ),
);
const ScoringSettings = React.lazy(
  () =>
    import(
      "@/modules/callIntelligence/features/Settings/components/ScoringSettings"
    ),
);
const NotificationAssignmentsPage = React.lazy(() =>
  import(
    "@/modules/callIntelligence/features/Settings/components/NotificationAssignments"
  ).then((module) => ({ default: module.NotificationAssignmentsPage })),
);
const RubricViewPermissionsPage = React.lazy(() =>
  import(
    "@/modules/callIntelligence/features/Settings/components/RubricViewPermissions"
  ).then((module) => ({ default: module.RubricViewPermissionsPage })),
);
const IntegrationsPage = React.lazy(
  () =>
    import(
      "@/modules/callIntelligence/features/Settings/components/IntegrationsPage"
    ),
);
const CallScoresPage = React.lazy(
  () =>
    import(
      "@/modules/transcripts/features/CallScoring/components/CallScoresPage/CallScoresPage"
    ),
);
const ScoringRulesPage = React.lazy(() =>
  import(
    "@/modules/callIntelligence/features/Settings/components/ScoringRules"
  ).then((module) => ({ default: module.ScoringRulesPage })),
);
const WebhooksPage = React.lazy(
  () =>
    import(
      "@/modules/administration/features/Webhooks/components/WebhooksPage"
    ),
);
const EmailTemplateSettings = React.lazy(
  () =>
    import(
      "@/modules/transcripts/features/EmailTemplateSettings/components/EmailTemplateSettings"
    ),
);
const TranscriptionQueueDashboard = React.lazy(
  () =>
    import(
      "@/modules/transcriptionQueue/features/Dashboard/components/TranscriptionQueueDashboard"
    ),
);
const MemberQuestionsPage = React.lazy(() =>
  import("@/modules/callIntelligence/features/MemberQuestions").then(
    (module) => ({ default: module.MemberQuestionsPage }),
  ),
);
const LeaderboardsPage = React.lazy(() =>
  import("@/modules/callIntelligence/features/Leaderboards").then((module) => ({
    default: module.LeaderboardsPage,
  })),
);
const CallSettingsPage = React.lazy(() =>
  import(
    "@/modules/callIntelligence/features/Settings/components/CallSettings"
  ).then((module) => ({ default: module.CallSettingsPage })),
);
const ScheduledReportsPage = React.lazy(() =>
  import("@/modules/callIntelligence/features/ScheduledReports").then(
    (module) => ({ default: module.ScheduledReportsPage }),
  ),
);

/**
 * CallIntelligenceRoutes
 *
 * Unified Call Intelligence platform combining:
 * - Call Scores & Reviews
 * - Transcription Queue Management
 * - Analytics & Alerts
 * - Settings & Configuration
 */
const CallIntelligenceRoutes = (
  <Route
    element={
      <AuthenticationGuard
        component={() => <MainLayout section="call-intelligence" />}
      />
    }
    path="/calls"
  >
    {/* Dashboard */}
    <Route element={<Navigate replace to="/calls/dashboard" />} index />
    <Route
      element={
        <PermissionGuard
          requiredRoles={[
            "ROLE_SUPER_ADMIN",
            "ROLE_SALES_MANAGER",
            "ROLE_SALES_REP",
            "ROLE_ADMIN",
            "ROLE_OPERATIONS",
            "ROLE_SUPPORT",
          ]}
        >
          <ErrorBoundaryWithFallback>
            <CallIntelligenceDashboard />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="dashboard"
    />

    {/* Call Scores - Split View with Instant Preview */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <CallScoresPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="scores"
    />

    {/* Leaderboards */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={[
            "ROLE_SUPER_ADMIN",
            "ROLE_ADMIN",
            "ROLE_SALES_MANAGER",
            "ROLE_SALES_REP",
          ]}
        >
          <ErrorBoundaryWithFallback>
            <LeaderboardsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="leaderboards"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={[
            "ROLE_SUPER_ADMIN",
            "ROLE_SALES_MANAGER",
            "ROLE_SALES_REP",
          ]}
        >
          <ErrorBoundaryWithFallback>
            <UnifiedCallDetail />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path=":callId"
    />

    {/* Questions (Member Questions) */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={[
            "ROLE_SUPER_ADMIN",
            "ROLE_ADMIN",
            "ROLE_SALES_MANAGER",
          ]}
        >
          <ErrorBoundaryWithFallback>
            <MemberQuestionsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="questions"
    />

    {/* Scheduled Reports */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={[
            "ROLE_SUPER_ADMIN",
            "ROLE_ADMIN",
            "ROLE_SALES_MANAGER",
          ]}
        >
          <ErrorBoundaryWithFallback>
            <ScheduledReportsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="scheduled-reports"
    />

    {/* Transcriptions (formerly Transcription Queue Dashboard) */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <TranscriptionQueueDashboard />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="transcriptions"
    />

    {/* Settings - Global Call Settings */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <CallSettingsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/global"
    />

    {/* Settings - Scoring */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <ScoringSettings />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/scoring"
    />

    {/* Settings - Rubric Assignments (unified page with tabs) */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <ScoringRulesPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/scoring/rules"
    />

    {/* Settings - Phone Number Rubric Mapping (legacy route, redirects to unified page) */}
    <Route
      element={<Navigate replace to="/calls/settings/scoring/rules" />}
      path="settings/scoring/phone-numbers"
    />

    {/* Settings - Notifications */}
    {/* Legacy route - redirect to new assignments page */}
    <Route
      element={
        <Navigate replace to="/calls/settings/notifications/assignments" />
      }
      path="settings/notifications/recipients"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <NotificationAssignmentsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/notifications/assignments"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <EmailTemplateSettings />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/notifications/templates"
    />

    {/* Settings - View Permissions */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <RubricViewPermissionsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/permissions"
    />

    {/* Settings - Integrations */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <IntegrationsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/integrations"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <IntegrationsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/integrations/zoom"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <IntegrationsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/integrations/fathom"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <IntegrationsPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/integrations/api-keys"
    />

    {/* Settings - Webhook Logs */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <WebhooksPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings/webhooks"
    />
  </Route>
);

export default CallIntelligenceRoutes;
